/* -------------- Common -------------- */

export const cultures = ["tr-TR", "en-US"] as const;

export type Culture = typeof cultures[number];

export const cultureLabels: Record<Culture, string> = {
	"tr-TR": "Türkçe",
	"en-US": "İngilizce",
};

export const VOLUNTEERS_ABOUT_PATH = "/volunteers-about";

export interface ApiResponse<T> {
	result: T;
	error: { code?: number; message?: string } | string | null;
	isSuccessful: boolean;
}

export type OperationStatus =
	| { type: "idle" }
	| { type: "loading"; operation: "add" | "fetch" | "update" | "delete" };

// The content is a single record: "missing" is the 404 of the GET, "error" any other failure.
export type VolunteersAboutLoadState = "loading" | "ready" | "missing" | "error";

/* -------------- GET (response) -------------- */

export interface VolunteersAboutLocalizable {
	volunteersAboutLocalizableId: string;
	culture: string;
	title: string;
	description?: string | null;
	imageUrl: string;
}

export interface VolunteersAboutDetail {
	aboutId: string;
	volunteersAboutLocalizable: VolunteersAboutLocalizable[];
}

export type VolunteersAboutGetApiResponse = ApiResponse<VolunteersAboutDetail>;

/* -------------- ADD (form = request) -------------- */

export interface VolunteersAboutLocalizableFormData {
	culture: Culture;
	title: string;
	description: string;
	image: File | null;
}

export interface VolunteersAboutFormData {
	volunteersAboutLocalizable: VolunteersAboutLocalizableFormData[];
}

export const getDefaultVolunteersAboutFormData = (): VolunteersAboutFormData => ({
	volunteersAboutLocalizable: cultures.map(culture => ({
		culture,
		title: "",
		description: "",
		image: null,
	})),
});

/* -------------- EDIT (form state, complete) -------------- */

export interface VolunteersAboutEditLocalizable extends VolunteersAboutLocalizableFormData {
	volunteersAboutLocalizableId: string;
	imageUrl: string;
}

export interface VolunteersAboutEditFormData {
	aboutId: string;
	volunteersAboutLocalizable: VolunteersAboutEditLocalizable[];
}

// The update endpoint can only change a culture that exists, so the form shows the stored cultures in the usual order.
export const toVolunteersAboutEditFormData = (detail: VolunteersAboutDetail): VolunteersAboutEditFormData => ({
	aboutId: detail.aboutId,
	volunteersAboutLocalizable: cultures.flatMap(culture => {
		const localizable = detail.volunteersAboutLocalizable.find(item => item.culture === culture);
		return localizable
			? [{
				volunteersAboutLocalizableId: localizable.volunteersAboutLocalizableId,
				culture,
				title: localizable.title,
				description: localizable.description ?? "",
				imageUrl: localizable.imageUrl,
				image: null,
			}]
			: [];
	}),
});

/* -------------- UPDATE (PATCH request, partial - only changed fields) -------------- */

export interface UpdateVolunteersAboutLocalizable {
	volunteersAboutLocalizableId: string;
	culture: string;
	title?: string;
	description?: string;
	image?: File;
}

export interface UpdateVolunteersAboutRequest {
	volunteersAboutLocalizable: UpdateVolunteersAboutLocalizable[];
}

/* -------------- Rules (kept in sync with the backend validator) -------------- */

export const VolunteersAboutRules = {
	titleMaxLength: 50,
	descriptionMaxLength: 500,
	textPattern: /^[\p{L}\p{N}\s.,;:!?'"()\-_/&%+#@]*$/u,
	// The description also takes the em dash (—) of texts copied from documents; the backend checks only its length.
	descriptionPattern: /^[\p{L}\p{N}\s.,;:!?'"()\-_/&%+#@—]*$/u,
	imageMaxFileSize: 20 * 1024 * 1024,
	imageMimeTypes: ["image/jpeg", "image/jpg", "image/png"],
	imageDimension: { width: 320, height: 320 },
} as const;
